import _ from 'underscore';
import Promise from 'bluebird';
import { useCallback, useContext, useMemo } from 'react';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  loadCampaign,
  saveCampaign,
  loadAdGroup,
  CAMPAIGN_NEGATIVE_REFERENCE_ID,
  loadBundleCountries,
  modelToViewModelForCampaign,
  readLocationCriterions,
  loadDisclaimerAssociations,
} from '@bingads-webui-campaign-react/search-campaign-data';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { globalizationModelSetup } from '@bingads-webui-campaign/ads-globalization';
import {
  getDefaultLanguage,
  updateLocationTargetsForCampaign,
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import {
  useKeywordPlannerService,
} from '@bingads-webui-campaign-react/keyword-planner-shared';
import {
  useKeywordSuggestion,
} from '@bingads-webui-campaign-react/copilot-keyword-planner';
import { getBudgetRange } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { handleKeywordSuggestionsWhenChanges, useSetKeywordSuggestionsData } from '../../shared/keyword-suggestion-util';

const defaultLocationTargets = {
  bidMode: false,
  currentLevelSettings: {
    initLocations: [],
    currentLocations: [],
    peopleInLocation: true,
    peopleSearchingForLocation: true,
    targetOption: 'bundle',
  },
  parentLevelSettings: {},
  useParentLevel: false,
};

const getDefaultBudget = (serverContext) => {
  const defaultBudget = getBudgetRange(serverContext.CurrentAccount.CurrencyCode).Daily.Default;
  const USDefaultBudget = serverContext.Permissions.IsDefaultBudgetExperimentationEnabled ? 25 : 20;
  return {
    Amount: serverContext.CurrentAccount.CurrencyCode === 'USD' ? USDefaultBudget : defaultBudget,
    Type: 'DailyStandard',
  };
};

const defaultConversionGoals = {
  goalScopeSelection: 'account-scope',
  goalSelection: [],
  originalConversionGoals: [],
};

const defaultDisclaimer = {
  originalSelectedDisclaimer: null,
  selectedDisclaimer: null,
};

const getDefaultCampaignSettings = serverContext => ({
  locationTargets: defaultLocationTargets,
  Budget: getDefaultBudget(serverContext),
  AutoGeneratedAssetsEnabled: false,
  conversionGoals: defaultConversionGoals,
  DisclaimerAdsEnabled: false,
  disclaimer: defaultDisclaimer,
});

const defaultAdGroup = {
  Name: 'Ad Group 1',
  AdGroupType: 'SearchStandard',
};

const defaultWebsites = [];

export const useCampaignSettingsRepository = () => {
  const i18n = useContext(I18nContext);
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const {
    CurrentLocale: locale,
    CurrentUser: { Market: market },
    DynamicPermissions,
    Permissions,
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const defaultName = generateNameSuggestionSuffix('Search-Campaign');
  const defaultLanguage = useMemo(() => getDefaultLanguage(permissions, locale), [permissions, locale]);

  const { state, send } = useWizardService();
  const {
    campaignId,
    adGroupId,
    websites: savedWebsites,
  } = state.context.wizardData;
  const { onRequest, onSuccess, onFailed } = useSetKeywordSuggestionsData({ send, permissions });

  const { getSearchIdea } = useKeywordPlannerService();

  let defaultAdgroupSettings = defaultAdGroup;
  if (permissions.IsDomesticSimplifiedChineseEnabled) {
    const { LANGUAGES: supportedLanguages } = globalizationModelSetup({ permissions, culture: locale });
    if (supportedLanguages.length !== 1) {
      activity.error('Feature changed!!! Please reconsider the default language setting of ad group');
    }
    defaultAdgroupSettings = {
      ...defaultAdgroupSettings,
      Language: supportedLanguages[0],
    };
  }

  const load = useCallback(() => {
    const loadPromises = {
      campaign: campaignId ? loadCampaign(cid, aid, campaignId) : Promise.resolve({ ...getDefaultCampaignSettings(serverContext), Name: defaultName, Languages: defaultLanguage }),
      adGroup: adGroupId ? loadAdGroup(cid, aid, campaignId, adGroupId) : Promise.resolve(defaultAdgroupSettings),
      locationTargets: campaignId ? readLocationCriterions(cid, aid, campaignId) : Promise.resolve(defaultLocationTargets),
      disclaimerAssociations: campaignId ? loadDisclaimerAssociations({ cid, aid, campaignId }) : Promise.resolve({ value: [] }),
    };

    return Promise.props(loadPromises)
      .then(async ({
        campaign: rawCampaign,
        adGroup,
        locationTargets,
        disclaimerAssociations,
      }) => {
        const bundleCountriesAndLocationCriterions = await loadBundleCountries({
          cid, aid, serverContext, language: rawCampaign.Languages,
        });

        const campaign = campaignId ? modelToViewModelForCampaign({
          campaignModel: rawCampaign,
          locationCriterionsModel: locationTargets ? locationTargets.value : [],
          bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
          disclaimerAssociations: disclaimerAssociations ? disclaimerAssociations.value : [],
        }) : updateLocationTargetsForCampaign({
          campaign: rawCampaign,
          i18n,
          market,
          permissions,
          isInit: true,
        });

        return Promise.resolve({
          campaign,
          adGroup,
          ...bundleCountriesAndLocationCriterions,
          websites: savedWebsites || defaultWebsites,
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load campaign settings failed: ${error}`);
        activity.signal(`onCampaignSettingsLoadFailed - ${JSON.stringify({
          campaignId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, adGroupId, aid, campaignId, cid, defaultLanguage, defaultName, i18n, market, permissions, savedWebsites, serverContext]);

  const recommendateRequest = useKeywordSuggestion();
  const save = useCallback(({ data }) => {
    const {
      campaign,
      adGroup,
      bundleCountries,
      bundleLocationCriterions,
      websites,
    } = data;

    // This is for creation scenario only
    campaign.Status = 'Paused';

    if (!campaign.Id) {
      campaign.Id = CAMPAIGN_NEGATIVE_REFERENCE_ID;
      campaign.AdGroups = [{ ...adGroup, Campaign: { Id: CAMPAIGN_NEGATIVE_REFERENCE_ID } }];
    }

    // handle early getsuggested keywords api call to improve performance
    handleKeywordSuggestionsWhenChanges({
      currentUrl: websites && websites[0],
      currentLocationSettings: _.property(['locationTargets', 'currentLevelSettings'])(campaign),
      currentLanguages: campaign && campaign.Languages,
      previousUrl: state.context.wizardData.previousUrl || [],
      previousLocation: state.context.wizardData.previousLocation || [],
      previousLanguages: state.context.wizardData.previousLanguages || [],
      bundleLocationCriterions,
      activity,
      onRequest,
      onSuccess,
      onFailed,
      recommendateRequest,
      getSearchIdea,
      send,
      permissions,
    });

    const saveCampaignPromise = () => saveCampaign({
      campaign,
      bundleCountries,
      bundleLocationCriterions,
      websites,
    }, {
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    });

    return saveCampaignPromise()
      .then((result) => {
        const campaignResult = _.omit(result.campaign, 'AdGroups');
        // This condition is to handle update scenario, which won't return the adGroup in bulk response
        const adGroupResult = adGroupId ? adGroup : result.campaign.AdGroups[0];

        // Only record the campaignId when creating a new campaign
        if (campaign.Id < 0) {
          activity.signal(`onSaveCampaignSucceeded - ${campaignId}`);
        }

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          campaignId: Number(campaignResult.Id),
          campaignName: campaignResult.Name,
          adGroupId: Number(adGroupResult.Id),
          websites,
        });

        return Promise.resolve({
          campaign: campaignResult,
          adGroup: adGroupResult,
          websites,
        });
      })
      .catch((error) => {
        const {
          serverErrors = [],
          systemErrors = [],
        } = error || {};

        // eslint-disable-next-line no-console
        console.error(`Save campaign settings failed: ${error}`);
        activity.signal(`onCampaignSettingsSaveFailed - ${JSON.stringify({
          data,
          error,
        })}`);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          error: {
            serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
            systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
          },
        });
      });
  }, [activity, adGroupId, campaignId, i18n, onFailed, onRequest, onSuccess, recommendateRequest, scenario.name, send, state.context.wizardData.previousLanguages, state.context.wizardData.previousLocation, state.context.wizardData.previousUrl, getSearchIdea, permissions]);

  return {
    load,
    save,
  };
};
